import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchStrategiesForPeriod, fetchStrategyOwners } from "../utils/restAPI";
import {
  setStrategiesForPeriod,
  setStrategyStoreLoading,
  setStrategyStoreError,
  switchPeriod,
  selectStrategiesForPeriod,
  selectStrategyStoreLoading,
  selectCurrentPeriod
} from "../Redux/Reducers/strategyStoreSlice";

const DEFAULT_PAGE_SIZE = 12;

const toKebabCase = (text) =>
  text?.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

const transformStrategyData = (data) =>
  data.map((strategy) => ({
    idStrategy: strategy.strategyId,
    slug: strategy.metadata?.slug,
    ownerSlug: toKebabCase(strategy.owner?.name),
    title: strategy.strategyName,
    description: strategy.description || "No description available.",
    chartData: (strategy.slices || []).map((slice) => ({
      date: slice.date,
      value: slice.normalizedNetNav,
      displayPercent: slice.displayPercent
    })),
    periodReturn: strategy.netNav ?? 0,
    tags: strategy.metadata?.tags,
    categories: strategy.metadata?.categories,
    sharpeRatio: strategy.sharpeRatio || 0, // handle null sharpe ratio value 
    owner: strategy.owner || null,
    startDate: strategy.startDate,
    endDate: strategy.endDate,
  }));

export const useStrategyStore = () => {
  const dispatch = useDispatch();
  const timePeriod = useSelector(selectCurrentPeriod);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const [filters, setFilters] = useState({
    tags: [],
    categories: [],
    owners: [],
    sortBy: "",
    sortDir: "",
  });
  const [availableOwners, setAvailableOwners] = useState([]);

  useEffect(() => {
    fetchStrategyOwners().then((res) => {
      if (res.success) setAvailableOwners(res.data);
    });
  }, []);

  // Queue for serializing requests - response order matches request order
  const requestQueueRef = useRef([]);
  const isRequestPendingRef = useRef(false);

  // Keep track of last displayed strategies to show during loading
  const lastStrategiesRef = useRef([]);

    const strategiesFromStore = useSelector(selectStrategiesForPeriod(timePeriod));
  const loading = useSelector(selectStrategyStoreLoading);

  // Use last strategies while loading a new period, otherwise use current
  const strategies = useMemo(() => {
    if (strategiesFromStore) {
      lastStrategiesRef.current = strategiesFromStore;
      return strategiesFromStore;
    }
    // While loading, show previous strategies with blur effect
    return loading ? lastStrategiesRef.current : [];
  }, [strategiesFromStore, loading]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [searchTerm]);

  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const sendRequest = useCallback(async ({ period, page, search, filters: reqFilters }) => {
    isRequestPendingRef.current = true;

    try {
      dispatch(setStrategyStoreError(null));
      dispatch(setStrategyStoreLoading(true));
      const response = await fetchStrategiesForPeriod(period, page, DEFAULT_PAGE_SIZE, search, "owner", undefined, undefined, null, null, reqFilters);

      if (response.success) {
        const transformed = transformStrategyData(response.data);
        dispatch(setStrategiesForPeriod({ period, strategies: transformed }));
        setPagination(response.pagination);
      } else {
        dispatch(setStrategyStoreError(response.message || "Failed to load strategies"));
      }
    } catch (error) {
      console.error(error);
      dispatch(setStrategyStoreError(error?.message || "Failed to load strategies"));
    } finally {
      dispatch(setStrategyStoreLoading(false));
      isRequestPendingRef.current = false;
    }
  }, [dispatch]);

  const processQueue = useCallback(async () => {
    if (isRequestPendingRef.current) return;
    const nextRequest = requestQueueRef.current.shift();
    if (!nextRequest) return;

    await sendRequest(nextRequest);

    if (requestQueueRef.current.length > 0) {
      processQueue();
    }
  }, [sendRequest]);

  const fetchStrategies = useCallback((period, page, search, activeFilters) => {
    requestQueueRef.current.push({ period, page, search, filters: activeFilters });
    processQueue();
  }, [processQueue]);

  useEffect(() => {
    fetchStrategies(timePeriod, currentPage, debouncedSearchTerm, filtersRef.current);
  }, [timePeriod, currentPage, debouncedSearchTerm, fetchStrategies]);

  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
    // Reset to page 1 when filters change
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", "1");
      return next;
    });
    fetchStrategies(timePeriod, 1, debouncedSearchTerm, newFilters);
  }, [timePeriod, debouncedSearchTerm, fetchStrategies, setSearchParams]);

  const handleTimePeriodChange = useCallback((_, newPeriod) => {
    if (newPeriod !== null) {
      dispatch(switchPeriod(newPeriod));
    }
  }, [dispatch]);

  const handlePageChange = useCallback((page) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(page));
      return next;
    });
  }, [setSearchParams]);

  return {
    loading,
    timePeriod,
    searchTerm,
    setSearchTerm,
    strategies,
    handleTimePeriodChange,
    pagination,
    currentPage,
    handlePageChange,
    filters,
    handleFiltersChange,
    availableOwners,
  };
};
